import type { DeliveryMethodValue } from './PeoplePicker.types';

/**
 * Determines whether a person should be disabled (non-selectable) in the people picker
 * based on the current delivery mode and their available contact info.
 */
export const isPersonDisabled = ({
  hasEmail,
  hasPhone,
  hasGuestKey = true,
  emailOnly,
  smsOnly,
  allowUnreachableSelection,
  enableSmsSelection,
  deliveryMethod,
}: Readonly<{
  hasEmail: boolean;
  hasPhone: boolean;
  hasGuestKey?: boolean;
  emailOnly: boolean;
  smsOnly: boolean;
  allowUnreachableSelection: boolean;
  enableSmsSelection: boolean;
  deliveryMethod?: DeliveryMethodValue;
}>): boolean => {
  if (!hasGuestKey) return true;
  if (emailOnly) return !hasEmail;
  if (smsOnly) return !hasPhone;
  if (allowUnreachableSelection) return false;
  if (!enableSmsSelection) return !hasEmail;

  switch (deliveryMethod) {
    case 'text':
      return !hasPhone;
    case 'email':
      return !hasEmail;
    default:
      return !(hasEmail || hasPhone);
  }
};

/**
 * Returns a sublabel describing missing contact info, or undefined when
 * the SMS delivery-method selector handles the sublabel instead.
 */
export const getSublabelText = ({
  smsOnly,
  emailOnly,
  hasPhone,
  enableSmsSelection,
  hasEmail,
  tq,
}: Readonly<{
  smsOnly: boolean;
  emailOnly: boolean;
  hasPhone: boolean;
  enableSmsSelection: boolean;
  hasEmail: boolean;
  tq: (strings: TemplateStringsArray, ...values: unknown[]) => string;
}>): string | undefined => {
  if (emailOnly) return hasEmail ? '' : tq`Missing email address`;
  if (smsOnly) return hasPhone ? '' : tq`Missing phone number`;
  if (enableSmsSelection) return undefined;
  return hasEmail ? '' : tq`Missing email address`;
};

/**
 * Resolves the effective delivery method from the channel mode flags.
 */
export const resolveDeliveryMethod = ({
  smsOnly,
  emailOnly,
  initialDeliveryMethod,
}: Readonly<{
  smsOnly: boolean;
  emailOnly: boolean;
  initialDeliveryMethod?: DeliveryMethodValue;
}>): DeliveryMethodValue => {
  if (smsOnly) return 'text';
  if (emailOnly) return 'email';
  return initialDeliveryMethod ?? 'both';
};
